import { Controller } from "@hotwired/stimulus";
import consumer from '../../../javascript/channels/consumer';
import type VotersGridController from '../voters_grid_component/voters_grid_component_controller';
import type { GridRowError } from '../voters_grid_component/voters_grid_component_controller';

// ── Cable payload types ───────────────────────────────────────────────────────

interface ValidationData {
  type: 'validation';
  status: 'start' | 'progress' | 'done' | 'error';
  processed?: number;
  total?: number;
  valid?: boolean;
  error_csv_url?: string;
  voters_validation_id?: number;
  errors_count?: number;
  list_errors?: string[];
  row_errors?: GridRowError[];
  row_errors_truncated?: boolean;
}

interface CommitData {
  type: 'commit';
  status: 'commit_start' | 'commit_progress' | 'commit_done' | 'commit_error';
  processed?: number;
  total?: number;
  voters_validation_id: number;
}

interface InvitationsData {
  type: 'invitations';
  status: 'invitations_start' | 'invitations_progress' | 'invitations_done' | 'invitations_error';
  processed?: number;
  total?: number;
  invitations?: number;
  voters_validation_id: number;
}

interface IngestionData {
  type: 'ingestion';
  status: 'done' | 'error';
  voters_validation_id?: number;
  row_count?: number;
}

type CableData = ValidationData | CommitData | InvitationsData | IngestionData;
type StatusKind = 'success' | 'warning' | 'error';

// The validating overlay is cleared by an ActionCable broadcast; the poller is
// the safety net for broadcasts lost to WebSocket drops or subscription races.
const VALIDATION_STATUS_POLL_MS = 5000;

interface ValidationStatusResponse {
  state?: string;
  status?: string;
  errors_count?: number;
  list_errors?: string[];
  error_csv_url?: string;
  row_errors?: GridRowError[];
  row_errors_truncated?: boolean;
}

// Fallback wording for the server's own staleness refusal (409), when it sends no message of its
// own. An edit the user just made says it with the UI instead — see markListStale.
const STALE_LIST_MESSAGE =
  'You changed the list after it was validated. Select Validate list again before adding these voters.';

// js.voter_list.empty_list, verbatim — the Voters page says this for the same empty list.
const EMPTY_LIST_MESSAGE =
  'Your voter list is empty. Add at least one voter, then select "Validate list" again.';

// Mirrors ValidationWorker#default_blank_weights: a blank weight is staged as 1 on Validate, and
// the typed rows live only in the browser, so the page writes the same default into them.
const WEIGHT_COLUMN = 'weight';
const DEFAULT_WEIGHT = '1';

const GRID_CONTROLLER_ID = 'append-voters--voters-grid-component--voters-grid-component';
const DROP_ZONE_CONTROLLER_ID = 'application--toolkit--drop-zone-component--drop-zone-component';

interface DropZoneController { removeFile(event: Event): void; }

// ── Fetch helpers ─────────────────────────────────────────────────────────────

const csrfToken = (): string => {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? (meta.getAttribute('content') || '') : '';
};

const postJSON = (url: string, payload: unknown): Promise<Record<string, unknown>> =>
  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    body: JSON.stringify(payload),
    credentials: 'same-origin',
  }).then(async (res) => {
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return Promise.reject({ status: res.status, body });
    return body as Record<string, unknown>;
  });

// ── Controller ────────────────────────────────────────────────────────────────

export default class extends Controller {
  static targets = [
    'methodSelector',
    'typingPanel',
    'googleSheetsPanel',
    'validateButton',
    'publicUrlInput',
    'overlay',
    'statusMessage',
    'progressBar',
    'progressLabel',
    'confirmButton',
    'downloadErrorsButton',
    'successMessage',
    'resultMessage',
    'mapperPanel',
    'csvUploadPanel',
    'csvDropZone',
  ];

  // ── Target declarations ──────────────────────────────────────────────────

  declare readonly typingPanelTarget: HTMLElement;
  declare readonly googleSheetsPanelTarget: HTMLElement;
  declare readonly validateButtonTarget: HTMLButtonElement;
  declare readonly publicUrlInputTarget: HTMLInputElement;
  declare readonly overlayTarget: HTMLElement;
  declare readonly statusMessageTarget: HTMLElement;
  declare readonly progressBarTarget: HTMLProgressElement;
  declare readonly progressLabelTarget: HTMLElement;
  declare readonly confirmButtonTargets: HTMLButtonElement[];
  declare readonly downloadErrorsButtonTarget: HTMLAnchorElement;
  declare readonly successMessageTarget: HTMLElement;
  declare readonly resultMessageTarget: HTMLElement;
  declare readonly mapperPanelTarget: HTMLElement;
  declare readonly csvUploadPanelTarget: HTMLElement;
  declare readonly csvDropZoneTarget: HTMLElement;

  declare readonly hasTypingPanelTarget: boolean;
  declare readonly hasGoogleSheetsPanelTarget: boolean;
  declare readonly hasValidateButtonTarget: boolean;
  declare readonly hasPublicUrlInputTarget: boolean;
  declare readonly hasStatusMessageTarget: boolean;
  declare readonly hasProgressBarTarget: boolean;
  declare readonly hasProgressLabelTarget: boolean;
  declare readonly hasDownloadErrorsButtonTarget: boolean;
  declare readonly hasSuccessMessageTarget: boolean;
  declare readonly hasResultMessageTarget: boolean;
  declare readonly hasMapperPanelTarget: boolean;
  declare readonly hasCsvUploadPanelTarget: boolean;
  declare readonly hasCsvDropZoneTarget: boolean;
  declare readonly hasConfirmButtonTarget: boolean;
  declare readonly hasOverlayTarget: boolean;

  // ── Private state ────────────────────────────────────────────────────────

  private validationId: number | null = null;
  private voterListUploadId: number | null = null;
  private validating: boolean = false;
  // True only between a passing validation and the commit it authorises. Any edit clears it, which
  // is what forces the list through Validate again instead of committing the validated snapshot.
  private listValidated: boolean = false;
  // Which confirm button started the commit, so the success copy doesn't promise notices that were skipped.
  private sendNotices: boolean = true;
  private channel: { unsubscribe(): void } | null = null;
  private importMethod: string | null = null;
  private statusPollTimer: number | null = null;
  private statusPollGeneration: number = 0;
  private beforeStreamRenderListener: ((event: Event) => void) | null = null;

  // ── Lifecycle ────────────────────────────────────────────────────────────

  connect(): void {
    this.validationId   = null;
    this.channel = this.createSubscription();
    this.beforeStreamRenderListener = this.handleBeforeStreamRender.bind(this);
    document.addEventListener('turbo:before-stream-render', this.beforeStreamRenderListener);
    this.resetUI();
    const checked = this.element.querySelector<HTMLInputElement>('input[name="append_import_method"]:checked');
    if (checked) {
      this.importMethod = checked.value;
      this.showMethodPanels(checked.value);
    } else {
      this.showMethodPanels(null);
    }
  }

  disconnect(): void {
    this.stopStatusPolling();

    if (this.beforeStreamRenderListener) {
      document.removeEventListener('turbo:before-stream-render', this.beforeStreamRenderListener);
      this.beforeStreamRenderListener = null;
    }

    if (this.channel) {
      this.channel.unsubscribe();
      this.channel = null;
    }
  }

  // ── Actions wired from the view ──────────────────────────────────────────

  selectMethod(event: Event): void {
    const method = (event.target as HTMLInputElement).value;
    this.importMethod = method;
    this.showMethodPanels(method);
    this.resetUI();
    if (method === 'typing_or_copying') this.gridController()?.focusFirstCell();
  }

  // The single Validate list control: the typing grid re-ingests its rows, while a CSV or Sheets
  // list is already staged server-side and only needs its validation run again.
  revalidate(event: Event): void {
    if (this.importMethod === 'typing_or_copying') {
      this.validateTypingList(event);
      return;
    }

    this.validateIngestionList(event);
  }

  // Any edit to a list that has already passed — in the typing or review grid, by any import method.
  handleListEdited(): void {
    if (!this.listValidated || this.validating) return;

    this.markListStale();
  }

  validateTypingList(event: Event): void {
    if (event && event.preventDefault) event.preventDefault();

    const grid = this.gridController();
    if (!grid) {
      this.showError('Please wait for the table to finish loading.');
      return;
    }

    grid.clearRowErrors();
    const gridRows = grid.gatherRows();
    if (!gridRows.length) {
      this.showError(EMPTY_LIST_MESSAGE);
      return;
    }

    if (this.hasValidateButtonTarget) this.validateButtonTarget.disabled = true;
    this.submitIngestion({ source: 'inline_rows', source_params: { rows: gridRows } });
  }

  private markGridErrors(data: ValidationData): void {
    const grid = this.gridController();
    if (!grid) return;

    grid.showRowErrors(data.row_errors ?? []);
  }

  // The sentences are the voter list's own — js.voter_list.contains_not_highlightable_errors and
  // js.voter_list.from_csv.validation_result_in_grid in en-US.yml. Same validation, same words: an
  // organizer who has met these errors on the Voters page must not have to read them twice.
  private invalidListMessage(data: ValidationData): string {
    // A whole-list rejection names its own reason and marks no cell, so the generic "fix the
    // marked rows" wording would send the organizer looking for a mistake that is not in the
    // table.
    const listErrors = data.list_errors ?? [];
    if (listErrors.length) return listErrors.join(' ');

    const count = this.errorsCount(data);
    const lead = count === 1
      ? 'There is an error in your list that must be fixed before continuing.'
      : `There are ${count} errors in your list that must be fixed before continuing.`;

    if (!this.gridController() || (data.row_errors ?? []).length === 0) {
      return count === 1
        ? `${lead} Select "Download List" to view the error and explanation in a spreadsheet. ` +
          'Then, fix the error, and verify by selecting "Validate List" again.'
        : `${lead} Select "Download List" to view the errors and explanations in a spreadsheet. ` +
          'Then, fix the errors, and verify by selecting "Validate List" again.';
    }

    // Not in the voter list's wording because only this flow caps how many rows it marks.
    const capped = data.row_errors_truncated
      ? ' More rows failed than we can mark — "Download List" has the full set.'
      : '';

    return count === 1
      ? `${lead} It is listed in the Errors column and highlighted in the table below — correct it ` +
        'there, then select "Validate List" again. You can also select "Download List" to review ' +
        `it in a spreadsheet.${capped}`
      : `${lead} They are listed in the Errors column and highlighted in the table below — ` +
        'correct them there, then select "Validate List" again. You can also select ' +
        `"Download List" to review them in a spreadsheet.${capped}`;
  }

  // The server's own tally (VotersValidation#errors_count, carried by the status poll) is what the
  // Voters page counts too. The marked rows are the fallback, so a payload without it still counts
  // errors rather than rows — a row can fail on more than one field.
  private errorsCount(data: ValidationData): number {
    const reported = data.errors_count ?? 0;
    if (reported > 0) return reported;

    const marked = data.row_errors ?? [];
    const fromRows = marked.reduce(
      (total, row) => total + Object.values(row.fields ?? {}).reduce((n, messages) => n + messages.length, 0),
      0,
    );

    return Math.max(fromRows, marked.length, 1);
  }

  private gridController(): VotersGridController | null {
    const gridElement = this.element.querySelector<HTMLElement>(`[data-controller~="${GRID_CONTROLLER_ID}"]`);
    if (!gridElement) return null;

    return this.application.getControllerForElementAndIdentifier(
      gridElement,
      GRID_CONTROLLER_ID,
    ) as VotersGridController | null;
  }

  handleGridCellError(event: Event): void {
    const detail = (event as CustomEvent<{ message?: string }>).detail || {};
    if (!detail.message) return;

    this.setResult(detail.message, 'error');
  }

  handleGridTruncated(event: Event): void {
    const detail = (event as CustomEvent<{ maxRows?: number }>).detail || {};
    this.setResult(
      `You can add at most ${detail.maxRows} voters at a time. Rows beyond that limit were not added.`,
      'warning',
    );
  }

  pickFile(event: CustomEvent<{ fileId: string; accessToken: string }>): void {
    const detail = (event && event.detail) || ({} as { fileId?: string; accessToken?: string });
    if (!detail.fileId || !detail.accessToken) {
      this.showError('Could not read file from Google Picker.');
      return;
    }
    this.submitIngestion({
      source: 'picker',
      source_params: { file_id: detail.fileId, access_token: detail.accessToken },
    });
  }

  handlePickerFilePicked(event: CustomEvent<{ fileId: string; accessToken: string }>): void {
    this.pickFile(event);
  }

  handleUrlSubmitted(event: CustomEvent<{ url: string }>): void {
    const url = event && event.detail && event.detail.url;
    if (!url) {
      this.showError('Please paste a Google Sheets URL.');
      return;
    }
    this.submitIngestion({ source: 'public_url', source_params: { url } });
  }

  submitPublicUrl(event: Event): void {
    if (event && event.preventDefault) event.preventDefault();
    const url = this.hasPublicUrlInputTarget ? this.publicUrlInputTarget.value.trim() : '';
    if (!url) {
      this.showError('Please paste a Google Sheets URL.');
      return;
    }
    this.submitIngestion({ source: 'public_url', source_params: { url } });
  }

  handleCsvFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input || input.type !== 'file') return;

    this.submitCsvUpload();
  }

  submitCsvUpload(): void {
    const input = this.csvFileInput();
    const file = input?.files?.[0];
    if (!file) {
      this.showError('Please select a CSV file.');
      return;
    }

    const formData = new FormData();
    formData.append('source', 'csv_upload');
    formData.append('file', file);

    this.resetUI();
    this.show(this.overlayTarget);
    this.setStatus('Uploading CSV...');

    const url = this.data.get('validate-url') || '';
    fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html, application/json',
        'X-CSRF-Token': csrfToken(),
      },
      credentials: 'same-origin',
      body: formData,
    })
      .then((res) => {
        const contentType = res.headers.get('Content-Type') || '';
        if (contentType.includes('text/vnd.turbo-stream.html')) {
          return res.text().then((html) => {
            const idMatch = html.match(/data-voter-list-upload-id="(\d+)"/);
            if (idMatch) this.voterListUploadId = parseInt(idMatch[1], 10);
            (window as any).Turbo.renderStreamMessage(html);
            this.hide(this.overlayTarget);
            if (this.hasCsvUploadPanelTarget) this.hide(this.csvUploadPanelTarget);
            if (this.hasMapperPanelTarget) this.show(this.mapperPanelTarget);
          });
        }
        return res.json().then((body: Record<string, unknown>) => {
          const msg = (body.error as string) || 'Could not process the CSV file. Please retry.';
          this.showError(msg);
          this.hide(this.overlayTarget);
          this.clearCsvFileInput();
        });
      })
      .catch(() => {
        this.showError('Could not upload the CSV file. Please retry.');
        this.hide(this.overlayTarget);
        this.clearCsvFileInput();
      });
  }

  confirmImport(event: Event & { params?: { sendNotices?: boolean } }): void {
    if (event && event.preventDefault) event.preventDefault();
    const sendNotices = event?.params?.sendNotices !== false;
    if (!this.validationId) {
      this.showError('No validation in progress.');
      return;
    }
    this.sendNotices = sendNotices;
    this.disableConfirmButton();
    // The commit is authorised and in flight; an edit from here on has nothing left to invalidate.
    this.listValidated = false;
    this.show(this.overlayTarget);
    this.setStatus('Adding voters to your election...');
    postJSON(this.data.get('confirm-url') || '', {
      voters_validation_id: this.validationId,
      send_notices: sendNotices,
    })
      .catch((err: { status?: number; body?: { error?: string } }) => {
        if (this.hasOverlayTarget) this.hide(this.overlayTarget);
        // 409 is the server's own staleness check: an edit already sent this list back for
        // re-validation, so leave the Add Voters buttons disabled and offer Validate instead.
        if (err && err.status === 409) {
          this.markListStale((err.body && err.body.error) || STALE_LIST_MESSAGE);
          return;
        }
        // The list itself is still validated — only the request failed.
        this.listValidated = true;
        this.enableConfirmButton();
        this.showError('Could not start the import. Please retry.');
      });
  }

  validateIngestionList(event: Event): void {
    if (event && event.preventDefault) event.preventDefault();
    if (!this.validationId) {
      this.showError('Ingestion state lost. Please start over.');
      return;
    }
    this.validating = true;
    this.listValidated = false;
    this.disableConfirmButton();
    if (this.hasMapperPanelTarget) this.hide(this.mapperPanelTarget);
    this.show(this.overlayTarget);
    this.setStatus('Validating voters...');
    postJSON(this.data.get('start-validation-url') || '', { voters_validation_id: this.validationId })
      .then(() => this.startStatusPolling())
      .catch((err: { body?: { error?: string } }) => {
        this.validating = false;
        this.hide(this.overlayTarget);
        if (this.hasMapperPanelTarget) this.show(this.mapperPanelTarget);
        const msg = (err && (err as any).body && (err as any).body.error) || 'Could not start validation. Please retry.';
        this.showError(msg);
      });
  }

  confirmMapping(event: Event): void {
    if (event && event.preventDefault) event.preventDefault();
    if (!this.voterListUploadId) {
      this.showError('Mapping state lost. Please start over.');
      return;
    }
    if (this.hasMapperPanelTarget) this.hide(this.mapperPanelTarget);
    this.show(this.overlayTarget);
    this.setStatus('Loading voters...');
    postJSON(
      this.data.get('confirm-mapping-url') || '',
      { voter_list_upload_id: this.voterListUploadId }
    )
      .then((response) => {
        this.validationId = response.voters_validation_id as number;
        // ValidationWorker broadcasts will drive the UI from here; the poller
        // is the fallback if any of those broadcasts get lost.
        this.startStatusPolling();
      })
      .catch((err: { body?: { error?: string } }) => {
        this.hide(this.overlayTarget);
        if (this.hasMapperPanelTarget) this.show(this.mapperPanelTarget);
        const msg = (err && (err as any).body && (err as any).body.error) || 'Could not start validation. Please retry.';
        this.showError(msg);
      });
  }

  // ── Internals ────────────────────────────────────────────────────────────

  private submitIngestion(payload: Record<string, unknown>): void {
    this.resetUI();
    this.show(this.overlayTarget);
    this.setStatus('Uploading spreadsheet...');

    const url = this.data.get('validate-url') || '';
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/vnd.turbo-stream.html, application/json',
        'X-CSRF-Token': csrfToken(),
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    })
      .then((res) => {
        const contentType = res.headers.get('Content-Type') || '';
        if (contentType.includes('text/vnd.turbo-stream.html')) {
          // Google Sheets path: Turbo Stream updates the mapper panel
          return res.text().then((html) => {
            // Extract voter_list_upload_id from the meta element in the stream
            // before Turbo processes it (renderStreamMessage is synchronous for
            // the DOM mutation but we read from the raw HTML to be safe).
            const idMatch = html.match(/data-voter-list-upload-id="(\d+)"/);
            if (idMatch) this.voterListUploadId = parseInt(idMatch[1], 10);
            (window as any).Turbo.renderStreamMessage(html);
            this.hide(this.overlayTarget);
            this.hide(this.googleSheetsPanelTarget);
            if (this.hasMapperPanelTarget) this.show(this.mapperPanelTarget);
          });
        }
        // JSON path (inline rows or error)
        return res.json().then((body: Record<string, unknown>) => {
          if (!res.ok) {
            const msg = (body.error as string) || 'Could not upload the spreadsheet. Please retry.';
            this.showError(msg);
            this.hide(this.overlayTarget);
            return;
          }
          // Inline rows are already chained to ValidationWorker server-side.
          // Track that job here instead of enqueueing the same validation again.
          this.validationId = body.voters_validation_id as number;
          this.validating = true;
          if (this.hasValidateButtonTarget) this.validateButtonTarget.disabled = true;
          this.setStatus('Validating voters...');
          this.startStatusPolling();
        });
      })
      .catch(() => {
        this.showError('Could not upload the spreadsheet. Please retry.');
        this.hide(this.overlayTarget);
      });
  }

  private csvFileInput(): HTMLInputElement | null {
    if (!this.hasCsvDropZoneTarget) return null;

    return this.csvDropZoneTarget.querySelector<HTMLInputElement>('input[type="file"]');
  }

  private clearCsvFileInput(): void {
    if (!this.hasCsvDropZoneTarget) return;

    this.dropZoneController()?.removeFile(new Event('reset'));

    const input = this.csvFileInput();
    if (input) input.value = '';
  }

  private dropZoneController(): DropZoneController | null {
    if (!this.hasCsvDropZoneTarget) return null;

    const element = this.csvDropZoneTarget.querySelector<HTMLElement>(
      `[data-controller~="${DROP_ZONE_CONTROLLER_ID}"]`,
    );
    if (!element) return null;

    return this.application.getControllerForElementAndIdentifier(
      element,
      DROP_ZONE_CONTROLLER_ID,
    ) as DropZoneController | null;
  }

  private createSubscription(): { unsubscribe(): void } {
    return consumer.subscriptions.create(
      {
        channel: 'AppendVotersChannel',
        user_id: this.data.get('user-id'),
      },
      {
        // A (re)connect may have missed broadcasts — reconcile immediately.
        connected: () => this.reconcileValidationStatus(),
        disconnected: () => {},
        received: this.cableReceived.bind(this),
      }
    );
  }

  private cableReceived(data: CableData): void {
    switch (data.type) {
      case 'ingestion': this.handleIngestion(data); break;
      case 'validation': this.handleValidation(data); break;
      case 'commit': this.handleCommit(data); break;
      case 'invitations': this.handleInvitations(data); break;
    }
  }

  private handleValidation(data: ValidationData, verifiedTerminal: boolean = false): void {
    if (this.staleAttempt(data.voters_validation_id)) return;

    switch (data.status) {
      case 'start':
        this.validating = true;
        this.gridController()?.clearRowErrors();
        if (this.hasValidateButtonTarget) this.validateButtonTarget.disabled = true;
        if (this.hasOverlayTarget) this.show(this.overlayTarget);
        this.setStatus('Validating voters...');
        this.updateProgress(0, 0);
        this.startStatusPolling();
        break;
      case 'progress':
        this.updateProgress(data.processed || 0, data.total || 0);
        break;
      case 'done':
        if (!verifiedTerminal) {
          this.reconcileValidationStatus();
          break;
        }
        this.validating = false;
        this.stopStatusPolling();
        this.fillBlankWeights();
        if (data.valid) {
          this.listValidated = true;
          this.enableConfirmButton();
          if (this.hasOverlayTarget) this.hide(this.overlayTarget);
          // Validate list belongs to the typing method; markListStale borrows it for a stale CSV or
          // Sheets list, so put it away again once that list passes.
          if (this.hasValidateButtonTarget && this.importMethod !== 'typing_or_copying') {
            this.hide(this.validateButtonTarget);
          }
          if (this.hasMapperPanelTarget && this.importMethod !== 'typing_or_copying') {
            this.show(this.mapperPanelTarget);
          }
          this.setResult(
            this.data.get('offer-add-voters-only') === 'true'
              ? 'Everything looks good! Select "Add Voters and Send Notices" or "Add Voters Only" to continue.'
              : 'Everything looks good! Select "Add Voters and Send Notices" to continue.',
            'success',
          );
        } else {
          this.listValidated = false;
          if (this.hasValidateButtonTarget) this.validateButtonTarget.disabled = false;
          if (this.hasOverlayTarget) this.hide(this.overlayTarget);
          this.markGridErrors(data);
          this.setResult(this.invalidListMessage(data), 'error');
          if (this.hasDownloadErrorsButtonTarget && data.error_csv_url) {
            this.downloadErrorsButtonTarget.setAttribute('href', data.error_csv_url);
            this.show(this.downloadErrorsButtonTarget);
          }
          if (this.hasMapperPanelTarget && this.importMethod !== 'typing_or_copying') {
            this.show(this.mapperPanelTarget);
          } else {
            // typing_or_copying or no mapper panel: keep confirm button disabled.
            this.disableConfirmButton();
          }
        }
        break;
      case 'error':
        if (!verifiedTerminal) {
          this.reconcileValidationStatus();
          break;
        }
        this.validating = false;
        this.stopStatusPolling();
        if (this.hasValidateButtonTarget) this.validateButtonTarget.disabled = false;
        if (this.hasOverlayTarget) this.hide(this.overlayTarget);
        this.showError('Validation failed. Please retry.');
        if (this.hasMapperPanelTarget) this.show(this.mapperPanelTarget);
        break;
    }
  }

  private handleIngestion(data: IngestionData): void {
    if (this.staleAttempt(data.voters_validation_id)) return;

    if (data.status !== 'done') {
      if (this.hasOverlayTarget) this.hide(this.overlayTarget);
      this.showError('Ingestion failed. Please start over.');
      return;
    }

    this.validationId = data.voters_validation_id as number;
    // Validation starts automatically server-side; keep the overlay visible
    // and update the status — handleValidation will manage transitions from here.
    this.setStatus('Validating voters...');
    this.startStatusPolling();
  }

  // The append_voters stream is per-USER: every attempt (each Validate click
  // creates a new VotersValidation with its own jobs) broadcasts into it, and
  // stale jobs from earlier attempts can fire minutes later. Only messages for
  // the attempt this page is tracking may drive the UI.
  private staleAttempt(votersValidationId?: number): boolean {
    return !votersValidationId ||
      !this.validationId ||
      votersValidationId !== this.validationId;
  }

  // ── Validation status reconciliation ─────────────────────────────────────
  //
  // The overlay is normally cleared by an ActionCable broadcast, but a broadcast
  // can be lost (WebSocket drop, broadcast racing the subscription, worker
  // no-op). While a validation is pending we poll the status endpoint and feed
  // any terminal state through the same handler the cable path uses.

  private startStatusPolling(): void {
    this.stopStatusPolling();
    this.statusPollGeneration += 1;
    this.statusPollTimer = window.setInterval(
      () => this.reconcileValidationStatus(),
      VALIDATION_STATUS_POLL_MS,
    );
  }

  private stopStatusPolling(): void {
    if (this.statusPollTimer !== null) {
      window.clearInterval(this.statusPollTimer);
      this.statusPollTimer = null;
    }
  }

  private reconcileValidationStatus(): void {
    const url = this.data.get('validation-status-url') || '';
    const validationId = this.validationId;
    const pollGeneration = this.statusPollGeneration;
    if (!url || !validationId || this.statusPollTimer === null) return;

    fetch(`${url}?voters_validation_id=${validationId}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((body: ValidationStatusResponse | null) => {
        if (
          !body ||
          this.statusPollTimer === null ||
          validationId !== this.validationId ||
          pollGeneration !== this.statusPollGeneration
        ) return;

        if (body.state === 'completed' && (body.status === 'valid' || body.status === 'invalid')) {
          this.handleValidation({
            type: 'validation',
            status: 'done',
            valid: body.status === 'valid',
            error_csv_url: body.error_csv_url,
            errors_count: body.errors_count,
            list_errors: body.list_errors,
            row_errors: body.row_errors,
            row_errors_truncated: body.row_errors_truncated,
            voters_validation_id: validationId,
          }, true);
        } else if (body.state === 'failed') {
          this.handleValidation({
            type: 'validation',
            status: 'error',
            voters_validation_id: validationId,
          }, true);
        }
        // created/running: work is still in flight — keep waiting.
      })
      .catch(() => { /* transient — the next poll retries */ });
  }

  private handleCommit(data: CommitData): void {
    if (this.staleAttempt(data.voters_validation_id)) return;

    switch (data.status) {
      case 'commit_start':
        if (this.hasOverlayTarget) this.show(this.overlayTarget);
        this.setStatus('Adding voters to your election...');
        break;
      case 'commit_progress':
        this.updateProgress(data.processed || 0, data.total || 0);
        this.setStatus(`Importing row ${data.processed} of ${data.total}...`);
        break;
      case 'commit_done':
        this.setStatus(this.sendNotices
          ? 'Voters successfully added! Sending invitations now...'
          : 'Voters successfully added! Finishing up...');
        break;
      case 'commit_error':
        if (this.hasOverlayTarget) this.hide(this.overlayTarget);
        this.setResult('Import failed. Please contact support.', 'error');
        break;
    }
  }

  private handleInvitations(data: InvitationsData): void {
    if (this.staleAttempt(data.voters_validation_id)) return;

    switch (data.status) {
      case 'invitations_start':
        this.setStatus('Preparing invitations...');
        break;
      case 'invitations_progress':
        this.updateProgress(data.processed || 0, data.total || 0);
        break;
      case 'invitations_done':
        if (this.hasOverlayTarget) this.hide(this.overlayTarget);
        if (this.hasSuccessMessageTarget) {
          this.successMessageTarget.textContent = this.sendNotices
            ? `Voters successfully added! ${data.invitations} invitations queued.`
            : `${data.invitations} ${data.invitations === 1 ? 'voter' : 'voters'} added. No notices were sent.`;
          this.show(this.successMessageTarget);
        }
        this.setResult('Voters successfully added!', 'success');
        window.location.href = this.data.get('success-url') || '';
        break;
      case 'invitations_error':
        if (this.hasOverlayTarget) this.hide(this.overlayTarget);
        this.setResult('Invitations could not be created. Please contact support.', 'error');
        break;
    }
  }

  // ── UI helpers ────────────────────────────────────────────────────────────

  private handleBeforeStreamRender(event: Event): void {
    const stream = event.target as HTMLElement | null;
    if (!stream || stream.getAttribute('target') !== 'append_voters_mapper_panel') return;

    const template = stream.querySelector('template') as HTMLTemplateElement | null;
    const incomingOverview = template?.content.querySelector<HTMLElement>('[data-voters-validation-id]');
    if (!incomingOverview) return;

    const incomingValidationId = Number(incomingOverview.dataset.votersValidationId);
    if (!this.validationId || incomingValidationId !== this.validationId) event.preventDefault();
  }

  private fillBlankWeights(): void {
    if (this.importMethod !== 'typing_or_copying') return;

    this.gridController()?.fillBlankCells(WEIGHT_COLUMN, DEFAULT_WEIGHT);
  }

  private showMethodPanels(method: string | null): void {
    if (this.hasTypingPanelTarget) {
      this.typingPanelTarget.classList.toggle('hide', method !== 'typing_or_copying');
    }
    if (this.hasGoogleSheetsPanelTarget) {
      this.googleSheetsPanelTarget.classList.toggle('hide', method !== 'google_sheets');
    }
    if (this.hasCsvUploadPanelTarget) {
      this.csvUploadPanelTarget.classList.toggle('hide', method !== 'csv_upload');
    }
    if (this.hasValidateButtonTarget) {
      this.validateButtonTarget.classList.toggle('hide', method !== 'typing_or_copying');
    }
  }

  startOver(): void {
    this.resetUI();
    const checked = this.element.querySelector<HTMLInputElement>('input[name="append_import_method"]:checked');
    if (checked) this.showMethodPanels(checked.value);
  }

  private resetUI(): void {
    this.stopStatusPolling();
    this.validationId = null;
    this.validating = false;
    this.listValidated = false;
    this.disableConfirmButton();
    if (this.hasDownloadErrorsButtonTarget) this.hide(this.downloadErrorsButtonTarget);
    if (this.hasSuccessMessageTarget) this.hide(this.successMessageTarget);
    if (this.hasOverlayTarget) this.hide(this.overlayTarget);
    if (this.hasResultMessageTarget) this.hide(this.resultMessageTarget);
    if (this.hasMapperPanelTarget) {
      this.hide(this.mapperPanelTarget);
      this.mapperPanelTarget.innerHTML = '';
    }
    this.voterListUploadId = null;
    if (this.hasValidateButtonTarget) this.validateButtonTarget.disabled = false;
    this.setStatus('');
    this.updateProgress(0, 0);
  }

  private setStatus(text: string, kind?: StatusKind): void {
    if (!this.hasStatusMessageTarget) return;
    this.statusMessageTarget.textContent = text || '';
    this.statusMessageTarget.classList.remove('inline_success', 'inline_error', 'inline_warning');
    if (kind === 'success') this.statusMessageTarget.classList.add('inline_success');
    if (kind === 'warning') this.statusMessageTarget.classList.add('inline_warning');
    if (kind === 'error') this.statusMessageTarget.classList.add('inline_error');
  }

  private showError(text: string): void {
    this.setResult(text, 'error');
  }

  private setResult(text: string, kind?: StatusKind): void {
    if (!this.hasResultMessageTarget) return;
    this.resultMessageTarget.textContent = text || '';
    this.resultMessageTarget.classList.remove('inline_success', 'inline_error', 'inline_warning');
    if (kind === 'success') this.resultMessageTarget.classList.add('inline_success');
    if (kind === 'warning') this.resultMessageTarget.classList.add('inline_warning');
    if (kind === 'error') this.resultMessageTarget.classList.add('inline_error');
    if (text) this.show(this.resultMessageTarget);
    else this.hide(this.resultMessageTarget);
  }

  private updateProgress(processed: number, total: number): void {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.hidden = !total;
      this.progressBarTarget.value = processed || 0;
      if (total) this.progressBarTarget.max = total;
    }
    if (this.hasProgressLabelTarget) {
      this.progressLabelTarget.textContent = total ? `${processed} / ${total}` : '';
    }
  }

  // Drops the verdict Add Voters leans on and puts Validate list back in reach — the Validate
  // button is hidden for the CSV and Sheets methods until a list of theirs goes stale.
  // An edit the user just made needs no words: the disabled Add Voters and the returning Validate
  // button say it. Only a stale list the server itself refuses arrives with something to explain.
  private markListStale(message?: string): void {
    this.listValidated = false;
    this.disableConfirmButton();
    this.setResult(message || '', message ? 'warning' : undefined);
    if (this.hasValidateButtonTarget) {
      this.validateButtonTarget.disabled = false;
      this.show(this.validateButtonTarget);
    }
  }

  private enableConfirmButton(): void {
    this.confirmButtonTargets.forEach((button) => {
      button.disabled = false;
      button.classList.remove('toolkit-button-component--disabled');
    });
  }

  private disableConfirmButton(): void {
    this.confirmButtonTargets.forEach((button) => {
      button.disabled = true;
      button.classList.add('toolkit-button-component--disabled');
    });
  }

  private show(el: HTMLElement | null): void {
    if (el && el.classList) el.classList.remove('hide');
  }

  private hide(el: HTMLElement | null): void {
    if (el && el.classList) el.classList.add('hide');
  }
}
